import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';

import { IconModule } from '@shared/icon/icon.module';
import { BillingCatalogApiService } from '../../../modules/subscription/services/billing-catalog-api.service';
import {
  formatPlanCreditsLabel,
} from '../../../modules/subscription/billing-subscription.models';
import { BillingSubscriptionStateService } from '../../../modules/subscription/services/billing-subscription-state.service';

type UpgradeSuggestion = {
  planCode: string;
  planName: string;
  aiCredits: string;
  priceLabel: string;
};

@Component({
  selector: 'app-trial-ended-modal',
  standalone: true,
  imports: [RouterLink, IconModule],
  template: `
    @if (state.showTrialEndedModal() && notice(); as trialNotice) {
      <div
        class="fixed inset-0 z-[1200] flex items-center justify-center bg-gray-900/50 px-4 backdrop-blur-[1px] dark:bg-black/60"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="trial-ended-title"
        aria-describedby="trial-ended-description"
      >
        <div
          class="w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-xl"
        >
          <div class="border-b border-border bg-primary/5 px-6 py-5">
            <div class="flex items-start gap-3">
              <span
                class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
                aria-hidden="true"
              >
                <icon-star class="h-5 w-5" />
              </span>
              <div>
                <h2 id="trial-ended-title" class="text-lg font-semibold text-foreground">
                  Your free trial has ended
                </h2>
                <p id="trial-ended-description" class="mt-1 text-sm text-muted-foreground">
                  Your workspace has been switched to the Free plan with lower AI credits and
                  limits.
                </p>
              </div>
            </div>
          </div>

          <div class="space-y-4 px-6 py-5">
            <div class="rounded-lg border border-border bg-muted/30 p-4 text-sm text-foreground">
              <p>
                <span class="font-medium">{{ formattedPreviousPlan() }}</span> trial ended on
                <span class="font-medium">{{ formattedEndedAt() }}</span>.
              </p>
              <p class="mt-2 text-muted-foreground">
                AI credits reduced from
                <span class="font-medium text-foreground">{{ previousCreditsLabel() }}/month</span>
                to
                <span class="font-medium text-foreground">{{ freeCreditsLabel() }}/month</span>
                on the Free plan.
              </p>
            </div>

            @if (upgradeSuggestions().length) {
              <div>
                <p class="text-sm font-medium text-foreground">Recommended upgrades</p>
                <ul class="mt-2 space-y-2">
                  @for (plan of upgradeSuggestions(); track plan.planCode) {
                    <li
                      class="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
                    >
                      <div class="min-w-0">
                        <p class="text-sm font-semibold text-foreground">{{ plan.planName }}</p>
                        <p class="text-xs text-muted-foreground">
                          {{ plan.aiCredits }} AI credits/month · {{ plan.priceLabel }}/user
                        </p>
                      </div>
                      <a
                        routerLink="/subscription"
                        class="shrink-0 text-xs font-medium text-primary hover:underline"
                        (click)="onUpgradeClick()"
                      >
                        View plan
                      </a>
                    </li>
                  }
                </ul>
              </div>
            }
          </div>

          <div class="flex flex-wrap items-center justify-end gap-2 border-t border-border px-6 py-4">
            <button
              type="button"
              class="btn btn-outline-primary rounded-full px-4 py-2 text-sm"
              (click)="dismiss()"
            >
              Maybe later
            </button>
            <a
              routerLink="/subscription"
              class="btn btn-primary rounded-full px-5 py-2 text-sm font-medium"
              (click)="onUpgradeClick()"
            >
              Upgrade now
            </a>
          </div>
        </div>
      </div>
    }
  `,
})
export class TrialEndedModalComponent {
  private readonly catalogApi = inject(BillingCatalogApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly state = inject(BillingSubscriptionStateService);
  readonly upgradeSuggestions = signal<UpgradeSuggestion[]>([]);

  readonly notice = computed(
    () => this.state.subscription()?.trialDowngradeNotice ?? null,
  );

  constructor() {
    this.catalogApi
      .getPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ plans }) => {
          const paidPlans = plans
            .filter((plan) => plan.planCode.toLowerCase() !== 'free')
            .slice(0, 3)
            .map((plan) => ({
              planCode: plan.planCode,
              planName: plan.planName,
              aiCredits: `${formatPlanCreditsLabel(plan.aiCreditsPerMonth)}/month`,
              priceLabel: `₹${Math.round(plan.monthlyPriceInr).toLocaleString('en-IN')}`,
            }));
          this.upgradeSuggestions.set(paidPlans);
        },
        error: () => {
          this.upgradeSuggestions.set([]);
        },
      });
  }

  formattedPreviousPlan(): string {
    return this.formatPlanName(this.notice()?.previousPlanName ?? 'Paid plan');
  }

  formattedEndedAt(): string {
    const endedAt = this.notice()?.endedAt;
    if (!endedAt) {
      return '—';
    }
    return new Date(endedAt).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  previousCreditsLabel(): string {
    return formatPlanCreditsLabel(
      this.notice()?.previousAiCreditsPerMonth ?? '0',
    );
  }

  freeCreditsLabel(): string {
    const credits = this.state.subscription()?.plan.aiCreditsPerMonth ?? '0';
    return formatPlanCreditsLabel(credits);
  }

  dismiss(): void {
    this.state.dismissTrialEndedModal();
  }

  onUpgradeClick(): void {
    this.state.dismissTrialEndedModal();
    void this.router.navigate(['/subscription']);
  }

  private formatPlanName(planName: string): string {
    const trimmed = planName.trim();
    if (!trimmed) {
      return 'Paid plan';
    }
    return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1).toLowerCase()}`;
  }
}
